import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Inbox } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { springs } from "../ui/motion";
import { InboxNotificationItem } from "./InboxNotificationItem";
import type { NotificationTab, NotificationWithStatus } from "./types";

interface Props {
  notifications: NotificationWithStatus[];
  selectedId: string | null;
  activeTab: NotificationTab;
  isArchived: boolean;
  focusedIndex: number | null;
  onTabChange: (tab: NotificationTab) => void;
  onSelect: (id: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onOpenRelated: (id: string) => void;
  isLoading: boolean;
}

export function InboxNotificationList({
  notifications,
  selectedId,
  activeTab,
  isArchived,
  focusedIndex,
  onTabChange,
  onSelect,
  onArchive,
  onUnarchive,
  onMarkRead,
  onMarkUnread,
  onOpenRelated,
  isLoading,
}: Props) {
  const { t } = useTranslation("inbox");
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (focusedIndex !== null && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [focusedIndex]);

  return (
    <div className="flex flex-col h-full">
      {/* Tab switcher */}
      <div className="flex gap-1 px-3 py-2 border-b border-border">
        {(["inbox", "archive"] as NotificationTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => onTabChange(tab)}
            className={`px-3 py-1 rounded-md text-[12px] font-medium transition-colors capitalize ${
              activeTab === tab
                ? "bg-accent-soft text-accent"
                : "text-secondary hover:text-primary hover:bg-card-hover/40"
            }`}
          >
            {tab === "inbox" ? t("tabs.inbox") : t("tabs.archive")}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {isLoading ? (
          <NotificationListSkeleton />
        ) : notifications.length === 0 ? (
          <EmptyState tab={activeTab} />
        ) : (
          <AnimatePresence mode="popLayout">
            {notifications.map((n, index) => (
              <div
                key={n.id}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
              >
                <InboxNotificationItem
                  notification={n}
                  isSelected={selectedId === n.id}
                  isArchived={isArchived}
                  isFocused={focusedIndex === index}
                  onSelect={onSelect}
                  onArchive={onArchive}
                  onUnarchive={onUnarchive}
                  onMarkRead={onMarkRead}
                  onMarkUnread={onMarkUnread}
                  onOpenRelated={onOpenRelated}
                  index={index}
                />
              </div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

function NotificationListSkeleton() {
  return (
    <div className="divide-y divide-border">
      {[1, 2, 3].map((i) => (
        <div key={i} className="px-3 py-3 flex gap-3 animate-pulse">
          <div className="mt-0.5 w-7 h-7 rounded-full bg-card-hover shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-card-hover rounded w-3/4" />
            <div className="h-3 bg-card-hover rounded w-1/2" />
            <div className="h-2.5 bg-card-hover rounded w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ tab }: { tab: NotificationTab }) {
  const { t } = useTranslation("inbox");
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={springs.bouncy}
      className="flex flex-col items-center gap-3 py-12 px-4 text-center"
    >
      <div className="w-10 h-10 rounded-full bg-accent-soft flex items-center justify-center">
        <Inbox size={18} className="text-accent" />
      </div>
      <p className="text-[13px] font-medium text-primary">
        {tab === "inbox"
          ? t("emptyState.allCaughtUp")
          : t("emptyState.nothingArchived")}
      </p>
      <p className="text-[12px] text-secondary">
        {tab === "inbox"
          ? t("emptyState.noNewNotifications")
          : t("emptyState.archivedItemsHere")}
      </p>
    </motion.div>
  );
}
